import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { ValidationProvider } from '@services/helpers/validation.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { TimeServiceProvider } from '@services/helpers/time-service/time.service';
import { ClientsDataServiceProvider } from '@services/client/clients.data.service';
import { TimeConverterProvider } from '@services/helpers/time-converter';
import { ClientServiceProvider } from '@services/client/client.service';

@Injectable()
export class AppointmentServiceProvider {
	appointmentType = 'APPOINTMENT';

	constructor(
		private validationService: ValidationProvider,
		private messageService: MessageService,
		private timeService: TimeServiceProvider,
		private timeConverter: TimeConverterProvider,
		private clientsDataService: ClientsDataServiceProvider,
		private clientService: ClientServiceProvider
	) {}

	private getClientId(job, clientId): number {
		if (job.primaryContact) {
			return job.primaryContact.id;
		}
		return clientId;
	}

	private isItemChosen(item): boolean {
		const itemChosen = item && item.id;
		return !!itemChosen;
	}

	private validateAppointment(appointment, isJobView) {
		let dateStart, dateEnd, timeStart, timeEnd;
		const validAppointment = Object.assign({}, appointment);

		if (this.validationService.isStringEmpty(validAppointment.name)) {
			this.messageService.showToaster('e_3', 'general', 'name');
			return false;
		}

		if (appointment.addToJob && !this.isItemChosen(appointment.jobSearch)) {
			this.messageService.showToaster('e_3', 'appointment');
			return false;
		}

		if (
			!isJobView &&
			appointment.sendEmails &&
			!appointment.addToJob &&
			!this.isItemChosen(appointment.clientSearch)
		) {
			this.messageService.showToaster('e_4', 'appointment');
			return false;
		}

		if (!validAppointment.startDate || !validAppointment.endDate) {
			this.messageService.showToaster('e_1', 'appointment');
			return false;
		}

		dateStart = this.timeService.getDateInFormat(
			validAppointment.startDate,
			'date'
		);
		dateEnd = this.timeService.getDateInFormat(
			validAppointment.endDate,
			'date'
		);

		const isCorrectDate = this.timeService.compareDates(dateEnd, dateStart);

		if (isCorrectDate) {
			this.messageService.showToaster('e_2', 'appointment');
			return false;
		}

		if (!validAppointment.allday) {
			const isStartIn24Format = this.timeService.isIn24Format(
				validAppointment.startTime
			);
			const isEndIn24Format = this.timeService.isIn24Format(
				validAppointment.endTime
			);
			timeStart = isStartIn24Format
				? validAppointment.startTime
				: this.timeConverter.convert12to24(validAppointment.startTime);
			timeEnd = isEndIn24Format
				? validAppointment.endTime
				: this.timeConverter.convert12to24(validAppointment.endTime);

			if (!timeStart || !timeEnd) {
				this.messageService.showToaster('e_0', 'appointment');
				return false;
			}

			if (dateStart === dateEnd) {
				const isCorrectTime = this.timeService.compareTimes(timeStart, timeEnd);

				if (!isCorrectTime) {
					this.messageService.showToaster('e_2', 'appointment');
					return false;
				}
			}
		}

		validAppointment.startDate = dateStart;
		validAppointment.endDate = dateEnd;
		validAppointment.startTime = timeStart;
		validAppointment.endTime = timeEnd;

		return validAppointment;
	}

	private prepareAppointmentTask(appointment) {
		appointment.calendarEvent = {
			id: appointment.calendarEvent ? appointment.calendarEvent.id : null,
			name: appointment.name,
			number: appointment.number,
			startDate: appointment.startDate,
			endDate: appointment.endDate,
			startTime: appointment.startTime,
			endTime: appointment.endTime,
			location: appointment.location,
			clientId: appointment.clientId,
			sendEmails: appointment.sendEmails
		};

		return appointment;
	}

	private createJobSearchObject(appointment) {
		const { jobId, jobName } = appointment;
		return {
			id: jobId,
			name: jobName
		};
	}

	prepareAppointmentToShow(event) {
		const appointment = Object.assign({}, event);

		const { clientId, jobId, startTime, sendEmails } = appointment;

		appointment.allday = !startTime;

		if (jobId) {
			appointment.jobSearch = this.createJobSearchObject(appointment);
			appointment.addToJob = true;
		}

		if (clientId && sendEmails) {
			return this.clientsDataService
				.getClient(clientId)
				.mergeMap((data: any) => {
					appointment.clientSearch = this.clientService.fillClientSearchText(
						data.contact
					);
					return Observable.of(appointment);
				});
		}
		return Observable.of(appointment);
	}

	prepareAppointmentToSave(appointment, isJobView, location) {
		let dataToSave = this.validateAppointment(appointment, isJobView);

		if (!dataToSave) {
			return false;
		}

		dataToSave.location = location;
		dataToSave.calendarEventType = this.appointmentType;

		const {
			addToJob,
			jobSearch,
			sendEmails,
			clientSearch,
			clientId
		} = dataToSave;

		if (sendEmails) {
			// if no job chosen
			if (clientSearch) {
				dataToSave.clientId = clientSearch.id;
				delete dataToSave.clientSearch;

				// get client from job
			} else if (jobSearch) {
				dataToSave.clientId = this.getClientId(jobSearch, clientId);

				//  get client when saving from job profile
			} else if (clientId) {
				dataToSave.clientId = clientId;
			} else {
				console.error('Can not find client');
			}
		}

		if (addToJob && jobSearch) {
			dataToSave.jobId = jobSearch.id;
		} else {
			delete dataToSave.jobId;
		}

		if (isJobView) {
			dataToSave = this.prepareAppointmentTask(dataToSave);
		}

		delete dataToSave.addToJob;
		delete dataToSave.jobSearch;
		delete dataToSave.jobName;
		return dataToSave;
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=kkml25e9zf!./shared/services/tasks/appointment.service.ts